import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { color, radius } from '../../theme/tokens';
import TableSkeleton from './TableSkeleton';
import EmptyState from './EmptyState';

/**
 * Reusable data table with a consistent header, loading skeleton, empty state
 * and responsive horizontal scrolling. Business logic stays in the pages.
 *
 * columns: [{ key, label, align, width, render(value, row), sx, hideBelow? }]
 */
const DataTable = ({
  columns,
  rows = [],
  rowKey = 'id',
  loading = false,
  skeletonRows = 6,
  emptyTitle = 'No records found',
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  onRowClick,
  size = 'medium',
  minWidth = 760,
  stickyHeader = false,
  getRowSx,
  sx,
}) => (
  <Box
    sx={{
      bgcolor: color.surface,
      border: `1px solid ${color.border}`,
      borderRadius: `${radius.xl}px`,
      overflow: 'hidden',
      ...sx,
    }}
  >
    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Table size={size} stickyHeader={stickyHeader} sx={{ minWidth }}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} align={col.align || 'left'} width={col.width} sx={col.sx}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableSkeleton rows={skeletonRows} columns={columns.length} size={size} />
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ p: 0, border: 0, '&:hover': { backgroundColor: 'transparent' } }}>
                <EmptyState
                  title={emptyTitle}
                  description={emptyDescription}
                  actionLabel={emptyActionLabel}
                  onAction={onEmptyAction}
                />
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, i) => (
              <TableRow
                key={typeof rowKey === 'function' ? rowKey(row) : (row[rowKey] ?? i)}
                hover={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  ...(getRowSx ? getRowSx(row) : {}),
                }}
              >
                {columns.map((col) => (
                  <TableCell key={col.key} align={col.align || 'left'} sx={col.sx}>
                    {col.render ? col.render(row[col.key], row) : (
                      row[col.key] === null || row[col.key] === undefined || row[col.key] === ''
                        ? <Typography component="span" sx={{ color: color.ink[400] }}>—</Typography>
                        : row[col.key]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

export default DataTable;
